import React from 'react';
import PrintLayout from './PrintLayout';

const CATEGORY_ICONS = {
  'Simple Process': (color) => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="2" width="14" height="2.5" rx="1.2" fill={color} />
      <rect x="2" y="7.75" width="14" height="2.5" rx="1.2" fill={color} />
      <rect x="2" y="13.5" width="9" height="2.5" rx="1.2" fill={color} />
    </svg>
  ),
  'Compelling Content': (color) => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <polygon points="9,1 11.5,6.5 17.5,7.3 13,11.5 14.2,17.5 9,14.5 3.8,17.5 5,11.5 0.5,7.3 6.5,6.5" fill={color} />
    </svg>
  ),
  'Healthy Behaviors': (color) => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 15.5C9 15.5 1.5 10.5 1.5 5.5C1.5 3.3 3.3 1.5 5.5 1.5C7 1.5 8.2 2.3 9 3.4C9.8 2.3 11 1.5 12.5 1.5C14.7 1.5 16.5 3.3 16.5 5.5C16.5 10.5 9 15.5 9 15.5Z" fill={color} />
    </svg>
  ),
  'Right Chemistry': (color) => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="7" r="4.5" fill={color} />
      <rect x="7" y="12" width="4" height="2" rx="1" fill={color} />
      <rect x="7.5" y="14.5" width="3" height="1.5" rx="0.75" fill={color} />
    </svg>
  ),
};

const CATEGORIES = [
  {
    label: 'Simple Process',
    color: '#2a3d5b',
    accent: 'rgba(42,61,91,0.08)',
    description:
      'The structural backbone of an effective meeting. Great meetings follow a clear process: topics are introduced purposefully, conversations stay on track, and every session ends with clear decisions and next steps. When the process works, nothing falls through the cracks.',
  },
  {
    label: 'Compelling Content',
    color: '#5f7942',
    accent: 'rgba(95,121,66,0.08)',
    description:
      'Meetings earn their place on the calendar only when they tackle issues that matter. Compelling content means the agenda contains topics worth debating, decisions worth making, and outcomes that directly advance the organization\'s top priorities.',
  },
  {
    label: 'Healthy Behaviors',
    color: '#cba43a',
    accent: 'rgba(203,164,58,0.08)',
    description:
      'Even the best agenda fails without the right behaviors in the room. Healthy teams build trust, share airtime fairly, challenge ideas without attacking people, and ask more questions than they make statements — keeping energy high and dialogue productive.',
  },
  {
    label: 'Right Chemistry',
    color: '#7209b7',
    accent: 'rgba(114,9,183,0.08)',
    description:
      'Every person on your team has natural strengths. When the right people are driving the right conversations at the right moments, meetings feel energizing rather than draining. This dimension tracks whether the team is playing to its collective strengths.',
  },
];

export default function PrintFrameworkIntro({ pageNumber }) {
  return (
    <PrintLayout pageNumber={pageNumber}>
      <div style={{
        position: 'absolute', top: 0, bottom: 68, left: 0, right: 0,
        display: 'flex', background: '#f5f4f0',
      }}>
        {/* Left panel */}
        <div style={{
          width: '38%', background: '#1e2e45',
          padding: '7% 6%', display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <div style={{
            fontSize: 8, fontWeight: 700, letterSpacing: '2.5px', color: '#5f7942',
            textTransform: 'uppercase', marginBottom: 14, fontFamily: 'Montserrat, sans-serif',
          }}>
            Meeting Analysis
          </div>
          <div style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 28, fontWeight: 700, color: '#fff', lineHeight: 1.15, marginBottom: 18,
          }}>
            A Framework for Great Meetings
          </div>
          <div style={{ width: 36, height: 2, background: '#5f7942', marginBottom: 20, borderRadius: 1 }} />
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', lineHeight: 1.75 }}>
            Great meetings don't happen by accident. They require the right structure, the right
            conversations, the right dynamics, and the right people contributing in the right ways.
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', lineHeight: 1.75, marginTop: 14 }}>
            This report evaluates your meeting across four interdependent dimensions — each one a
            critical ingredient in turning time together into meaningful progress.
          </div>
        </div>

        {/* Right panel — four category cards */}
        <div style={{
          flex: 1, padding: '5% 5%',
          display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 14,
        }}>
          {CATEGORIES.map(({ label, color, accent, description }) => (
            <div key={label} style={{
              background: 'white', borderRadius: 10,
              border: `1px solid rgba(42,61,91,0.1)`,
              borderLeft: `4px solid ${color}`,
              padding: '14px 18px',
              display: 'flex', gap: 14, alignItems: 'flex-start',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, background: accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {CATEGORY_ICONS[label]?.(color)}
              </div>
              <div>
                <div style={{
                  fontSize: 11, fontWeight: 700, color, marginBottom: 5,
                  fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.02em',
                }}>
                  {label}
                </div>
                <div style={{ fontSize: 9.5, color: '#4a5568', lineHeight: 1.65 }}>
                  {description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PrintLayout>
  );
}
